import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, Loader2, Plus } from "lucide-react";
import { api, unwrapArray } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { formatINR } from "@/lib/utils";
import { OrderStatusBadge } from "@/pages/orders/OrderStatusBadge";
import type { OrderRecord } from "@/pages/orders/orderTypes";
import type { QuotationRecord } from "@/types";

/** A deal's orders, and the button that makes one: from the accepted quote, or from a Won deal's products */
export function DealOrdersCard({ dealId, isWon }: { dealId: string; isWon: boolean }) {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { hasPermission } = useAuth();
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [accepted, setAccepted] = useState<QuotationRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const canRead = hasPermission("order.read");
  const canCreate = hasPermission("order.create");

  const load = async () => {
    try {
      const [o, q] = await Promise.all([
        api.get(`/api/SalesOrder/ByDeal/${dealId}`),
        hasPermission("quote.read") ? api.get(`/api/Quotation/List?deal_id=${dealId}&status=ACCEPTED&page_size=5`).catch(() => null) : Promise.resolve(null),
      ]);
      setOrders(unwrapArray<OrderRecord>(o));
      setAccepted(q ? unwrapPage<QuotationRecord>(q).rows[0] ?? null : null);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (canRead) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, canRead]);

  if (!canRead) return null;

  const live = orders.some((o) => o.order_status !== "CANCELLED" && (accepted ? o.quotation_id === accepted.id : !o.quotation_id));
  const canMake = canCreate && !live && (accepted || isWon);
  const make = async () => {
    setCreating(true);
    try {
      const res: any = await api.post(accepted ? `/api/SalesOrder/FromQuote/${accepted.id}` : `/api/SalesOrder/FromDeal/${dealId}`, {});
      success("Order created", "Add the customer's PO number, then confirm it");
      const created = res?.AddtionalData ?? res?.data ?? res;
      navigate(`/orders/${created?.id}`);
    } catch (e: any) {
      error("Could not create the order", e?.message || "Try again");
      load();
    } finally {
      setCreating(false);
    }
  };

  if (!loading && orders.length === 0 && !canMake) return null;

  return (
    <div className="bg-card border rounded-2xl shadow-xs overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <ClipboardList className="size-4 text-primary" />
          <h3 className="text-sm font-bold text-foreground">Orders</h3>
          {orders.length > 0 && <span className="text-[10px] px-1.5 rounded-full bg-muted text-muted-foreground">{orders.length}</span>}
        </div>
        {canMake && (
          <Button size="sm" variant="outline" onClick={make} disabled={creating} className="h-8 gap-1.5 text-xs">
            {creating ? <Loader2 className="size-3.5 animate-spin" /> : <Plus className="size-3.5" />}
            {accepted ? `Create order from ${accepted.display_number}` : "Create order"}
          </Button>
        )}
      </div>
      {loading ? (
        <div className="py-6 flex justify-center"><Loader2 className="size-5 animate-spin text-muted-foreground" /></div>
      ) : orders.length === 0 ? (
        <div className="py-5 px-4 text-center text-xs text-muted-foreground">No orders yet.</div>
      ) : (
        <ul className="divide-y">
          {orders.map((o) => (
            <li key={o.id}>
              <button type="button" onClick={() => navigate(`/orders/${o.id}`)} className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-muted/50">
                <div>
                  <div className="font-mono text-xs font-semibold">{o.order_number}</div>
                  <div className="text-[11px] text-muted-foreground">{o.customer_po_number ? `PO ${o.customer_po_number}` : "No PO yet"}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold">{formatINR(o.grand_total)}</span>
                  <OrderStatusBadge status={o.order_status} />
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
